/* eslint-disable max-lines */
import { type ChipProps } from "@mui/material";
import { API_TYPES } from "@src/appV2/api";
import { DayOfWeek } from "@src/appV2/InterviewSettings/constants";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { PlacementCandidateStatus } from "@src/appV2/PlacementCandidates/types";
import { InterviewStatus } from "@src/appV2/Placements/types/fetchPlacements.schema";
import { z } from "zod";

// eslint-disable-next-line no-restricted-syntax
export enum PlacementType {
  UNVERIFIED = "unverified",
  VERIFIED = "verified",
}

// eslint-disable-next-line no-restricted-syntax
export enum PlacementApplicationStatus {
  APPLIED = "applied",
  ACCEPTED = "accepted",
  INTERVIEW = "interview",
  INTERVIEW_NOT_CONFIRMED = "interview:notConfirmed",
  INTERVIEW_CONFIRMED = "interview:confirmed",
  INTERVIEW_CANCELLED = "interview:cancelled",
  INTERVIEW_NOT_SHOWED = "interview:notShowed",
  INTERVIEW_HELD = "interview:held",
  TURNED_AWAY = "interview:turnedAway",
  OFFER = "offer",
  OFFER_REJECTED = "offer:rejected",
  HIRED = "hired",
  ONBOARDING = "hired:onboarding",
  RETAINED = "hired:retained",
  RESIGNED = "hired:resigned",
  REJECTED = "rejected",
}

export const placementApplicationStatusMap: Record<
  PlacementApplicationStatus,
  {
    label: string;
    value: PlacementApplicationStatus;
    color: ChipProps["color"];
    groupedBy?: PlacementApplicationStatus;
  }
> = {
  [PlacementApplicationStatus.APPLIED]: {
    label: "Needs review",
    value: PlacementApplicationStatus.APPLIED,
    color: "accent" as ChipProps["color"],
  },
  [PlacementApplicationStatus.ACCEPTED]: {
    label: "Qualified",
    value: PlacementApplicationStatus.ACCEPTED,
    color: "warning" as ChipProps["color"],
  },
  [PlacementApplicationStatus.INTERVIEW]: {
    label: "Interview",
    value: PlacementApplicationStatus.INTERVIEW,
    color: "success" as ChipProps["color"],
  },
  [PlacementApplicationStatus.INTERVIEW_NOT_CONFIRMED]: {
    label: "Interview not confirmed",
    value: PlacementApplicationStatus.INTERVIEW_NOT_CONFIRMED,
    color: "warning" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.INTERVIEW,
  },
  [PlacementApplicationStatus.INTERVIEW_CONFIRMED]: {
    label: "Interview confirmed",
    value: PlacementApplicationStatus.INTERVIEW_CONFIRMED,
    color: "success" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.INTERVIEW,
  },
  [PlacementApplicationStatus.INTERVIEW_CANCELLED]: {
    label: "Interview cancelled",
    value: PlacementApplicationStatus.INTERVIEW_CANCELLED,
    color: "error" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.INTERVIEW,
  },
  [PlacementApplicationStatus.INTERVIEW_NOT_SHOWED]: {
    label: "Interview no Showed",
    value: PlacementApplicationStatus.INTERVIEW_NOT_SHOWED,
    color: "error" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.INTERVIEW,
  },
  [PlacementApplicationStatus.INTERVIEW_HELD]: {
    label: "Interview held",
    value: PlacementApplicationStatus.INTERVIEW_HELD,
    color: "success" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.INTERVIEW,
  },
  [PlacementApplicationStatus.TURNED_AWAY]: {
    label: "Turned away",
    value: PlacementApplicationStatus.TURNED_AWAY,
    color: "error" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.INTERVIEW,
  },
  [PlacementApplicationStatus.OFFER]: {
    label: "Offer",
    value: PlacementApplicationStatus.OFFER,
    color: "success" as ChipProps["color"],
  },
  [PlacementApplicationStatus.OFFER_REJECTED]: {
    label: "Offer rejected",
    value: PlacementApplicationStatus.OFFER_REJECTED,
    color: "error" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.OFFER,
  },
  [PlacementApplicationStatus.HIRED]: {
    label: "Hired",
    value: PlacementApplicationStatus.HIRED,
    color: "success" as ChipProps["color"],
  },
  [PlacementApplicationStatus.ONBOARDING]: {
    label: "Onboarding",
    value: PlacementApplicationStatus.ONBOARDING,
    color: "info" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.HIRED,
  },
  [PlacementApplicationStatus.RETAINED]: {
    label: "Retained",
    value: PlacementApplicationStatus.RETAINED,
    color: "success" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.HIRED,
  },
  [PlacementApplicationStatus.RESIGNED]: {
    label: "Resigned",
    value: PlacementApplicationStatus.RESIGNED,
    color: "error" as ChipProps["color"],
    groupedBy: PlacementApplicationStatus.HIRED,
  },
  [PlacementApplicationStatus.REJECTED]: {
    label: "Disqualified",
    value: PlacementApplicationStatus.REJECTED,
    color: "error" as ChipProps["color"],
  },
};
export type PlacementApplicationStatusValue =
  typeof placementApplicationStatusMap[keyof typeof placementApplicationStatusMap];

export const getParentStatus = (status: PlacementApplicationStatus): PlacementApplicationStatus => {
  if (!status.includes(":")) {
    return status;
  }

  return status.split(":")[0] as PlacementApplicationStatus;
};

export const getStatusGroup = (status: PlacementApplicationStatus) => {
  const groupedStatuses = Object.values(placementApplicationStatusMap)
    .filter((groupedStatus) => groupedStatus.groupedBy === status)
    .map((status) => status.value);
  return [status, ...groupedStatuses];
};

export const placementApplicationDataSchema = z.object({
  type: z.literal(API_TYPES.placementApplication),
  id: z.string(),
  attributes: z
    .object({
      status: z.nativeEnum(PlacementApplicationStatus),
      placementType: z.nativeEnum(PlacementType).optional(),
      placementTier: z.string().optional(),
      appliedDate: z.string(),
      notes: z.string().optional(),
      distanceInMiles: z.number().optional(),
      workerVoiceNoteUrl: z.string().optional(),
      applicationPacketUrl: z.string().optional(),
      workerApplicationPreferenceDetails: z
        .object({
          yearsOfExperienceByQualification: z.record(z.string(), z.number()),
          notes: z.string(),
          preferredDaysOfWeek: z.array(z.nativeEnum(DayOfWeek)),
        })
        .optional(),
      workerInterviewRequest: z
        .object({
          dates: z.array(z.coerce.date()),
          notes: z.string(),
        })
        .optional(),
    })
    .strict(),
  relationships: z.object({
    placement: z.object({
      data: z.object({
        type: z.literal(API_TYPES.placement),
        id: z.string(),
      }),
    }),
    placementCandidate: z.object({
      data: z.object({
        type: z.literal(API_TYPES.placementCandidate),
        id: z.string(),
      }),
    }),
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
    workerRequirements: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.workerRequirement),
            id: z.string(),
          })
        ),
      })
      .optional(),
  }),
});
export type PlacementApplicationData = z.infer<typeof placementApplicationDataSchema>;

const placementApplicationPlacementIncludeSchema = z.object({
  type: z.literal(API_TYPES.placement),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    description: z.string().optional(),
    workerTypes: z.array(z.string()),
  }),
  relationships: z.object({
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
  }),
});
export type PlacementApplicationIncludedPlacement = z.infer<
  typeof placementApplicationPlacementIncludeSchema
>;

const placementApplicationWorkplaceIncludeSchema = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    placementSettings: z.object({
      isProspect: z.boolean(),
    }),
  }),
});
export type PlacementApplicationIncludedWorkplace = z.infer<
  typeof placementApplicationWorkplaceIncludeSchema
>;

const placementApplicationPlacementCandidateIncludeSchema = z.object({
  type: z.literal(API_TYPES.placementCandidate),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(PlacementCandidateStatus),
    accessId: z.string(),
  }),
  relationships: z.object({
    worker: z.object({
      data: z.object({
        type: z.literal(API_TYPES.worker),
        id: z.string(),
      }),
    }),
  }),
});
export type PlacementApplicationIncludedPlacementCandidate = z.infer<
  typeof placementApplicationPlacementCandidateIncludeSchema
>;

const placementApplicationWorkerIncludeSchema = z.object({
  type: z.literal(API_TYPES.worker),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    profileImageUrl: z.string().optional(),
    completedShifts: z.number().optional(),
  }),
});
export type PlacementApplicationIncludedWorker = z.infer<
  typeof placementApplicationWorkerIncludeSchema
>;

const placementApplicationInterviewIncludeSchema = z.object({
  type: z.literal(API_TYPES.interview),
  id: z.string(),
  attributes: z.object({
    type: z.nativeEnum(InterviewType),
    start: z.string(),
    end: z.string(),
    status: z.nativeEnum(InterviewStatus),
    placementApplicationId: z.string(),
    attendance: z
      .object({
        method: z.string(),
        confirmedAt: z.coerce.date(),
        confirmedBy: z.string().optional(),
      })
      .optional(),
    attendanceBonus: z
      .object({
        bonusId: z.string(),
        amount: z.object({
          amountInMinorUnits: z.number(),
          currencyCode: z.string(),
        }),
        createdAt: z.coerce.date(),
        createdBy: z.string().optional(),
        createdByUserType: z.string(),
      })
      .optional(),
  }),
});
export type PlacementApplicationIncludedInterview = z.infer<
  typeof placementApplicationInterviewIncludeSchema
>;

// eslint-disable-next-line no-restricted-syntax
export enum WorkerRequirementStatus {
  REJECTED = "Rejected",
  EXPIRED = "Expired",
  EXPIRING = "Expiring Soon",
  MISSING = "Missing",
  IN_REVIEW = "In Review",
  APPROVED = "Approved",
}

const placementApplicationWorkerRequirementIncludeSchema = z.object({
  type: z.literal(API_TYPES.workerRequirement),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(WorkerRequirementStatus),
    expiry: z.string().optional(),
    isValid: z.boolean(),
    qualifications: z.array(z.string()),
    requiredByWorkplacesCount: z.number().optional(),
  }),
  relationships: z.object({
    requirement: z.object({
      data: z.object({
        type: z.literal(API_TYPES.requirement),
        id: z.string(),
        name: z.string().optional(),
      }),
    }),
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
  }),
});
export type PlacementApplicationIncludedWorkerRequirement = z.infer<
  typeof placementApplicationWorkerRequirementIncludeSchema
>;

export const placementApplicationResponseSchema = z.object({
  data: placementApplicationDataSchema,
  included: z.array(
    z.union([
      placementApplicationPlacementIncludeSchema,
      placementApplicationWorkplaceIncludeSchema,
      placementApplicationPlacementCandidateIncludeSchema,
      placementApplicationWorkerIncludeSchema,
      placementApplicationInterviewIncludeSchema,
      placementApplicationWorkerRequirementIncludeSchema,
    ])
  ),
});
export type PlacementApplicationResponse = z.infer<typeof placementApplicationResponseSchema>;

export const placementApplicationsResponseSchema = z.object({
  data: z.array(placementApplicationResponseSchema.shape.data),
  included: placementApplicationResponseSchema.shape.included,
  links: z.object({
    nextCursor: z.string().optional(),
  }),
});
export type PlacementApplicationsResponse = z.infer<typeof placementApplicationsResponseSchema>;

export const getPlacementApplicationSummariesQuerySchema = z.object({
  filter: z
    .object({
      "workplace.id": z.string().optional(),
    })
    .optional(),
});

export const PlacementApplicationSummariesSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.placementApplicationSummary),
    attributes: z.object({
      applied: z.number(),
      accepted: z.number(),
      interview: z.number(),
      "interview:notConfirmed": z.number(),
      "interview:confirmed": z.number(),
      "interview:cancelled": z.number(),
      "interview:notShowed": z.number(),
      "interview:held": z.number(),
      "interview:turnedAway": z.number(),
      offer: z.number(),
      "offer:rejected": z.number(),
      hired: z.number(),
      "hired:onboarding": z.number(),
      "hired:retained": z.number(),
      "hired:resigned": z.number(),
      rejected: z.number(),
    }),
  }),
});

export type PlacementApplicationSummariesResponse = z.infer<
  typeof PlacementApplicationSummariesSchema
>;

export type PlacementApplicationSummariesStatus =
  keyof PlacementApplicationSummariesResponse["data"]["attributes"];
/* eslint-enable max-lines */
